import { useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Snackbar, Tooltip } from "@mui/material";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import { toCanvas } from "html-to-image";
import { factsheetShareUrl } from "../../data/factsheets";

const MAX_CANVAS_PIXELS = 16_000_000;
const PAGE_MARGIN = 24; // pt, ~8.5mm
const WHITE = 246; // channel value above which a pixel counts as background
const MIN_GAP_ROWS = 10; // blank rows needed before we accept a cut
const BREAK_SEARCH = 0.35; // how far back up a page we hunt for that gap
const MIN_PAGE_FILL = 0.4; // never end a page emptier than this

// Slicing the capture at fixed intervals cuts straight through headings and charts, so
// walk back up from the ideal break until we find a run of blank rows and cut there.
// The factsheet has coloured rails down both edges that are never blank, hence sampling
// only the middle of each row.
const findPageBreaks = (canvas, sliceHeight) => {
    const context = canvas.getContext("2d", { willReadFrequently: true });
    const left = Math.floor(canvas.width * 0.12);
    const sampleWidth = Math.max(1, Math.floor(canvas.width * 0.76));
    const breaks = [];
    let start = 0;

    while (start < canvas.height) {
        const ideal = Math.min(start + sliceHeight, canvas.height);
        if (ideal >= canvas.height) {
            breaks.push(canvas.height);
            break;
        }

        const limit = Math.max(
            Math.round(start + sliceHeight * MIN_PAGE_FILL),
            Math.round(ideal - sliceHeight * BREAK_SEARCH)
        );
        const rows = context.getImageData(left, limit, sampleWidth, ideal - limit + 1).data;

        let cut = ideal;
        let run = 0;
        for (let y = ideal - limit; y >= 0; y -= 1) {
            let blank = true;
            for (let x = 0; x < sampleWidth; x += 1) {
                const pixel = (y * sampleWidth + x) * 4;
                if (rows[pixel] < WHITE || rows[pixel + 1] < WHITE || rows[pixel + 2] < WHITE) {
                    blank = false;
                    break;
                }
            }
            if (!blank) {
                run = 0;
            } else if ((run += 1) >= MIN_GAP_ROWS) {
                cut = limit + y + run - 1; // bottom of the gap: where content resumes
                break;
            }
        }

        breaks.push(cut);
        start = cut;
    }

    return breaks;
};

// Toolbar plus the factsheet itself, shared by the dialog on the indicator page and the
// standalone /factsheet/:iso3 route, so both offer print, PDF and the shareable link.
export default function FactsheetViewer({ iso3, url, title, height, endActions }) {
    const iframeRef = useRef(null);
    const [generatingPdf, setGeneratingPdf] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    // The factsheet is served from our own origin, so we can drive the iframe's own
    // print dialog and get the document alone instead of the whole dashboard page.
    const printFactsheet = () => {
        const frame = iframeRef.current?.contentWindow;
        if (!frame) return;
        frame.focus();
        frame.print();
    };

    const copyLink = () => {
        navigator.clipboard.writeText(factsheetShareUrl(iso3));
        setLinkCopied(true);
    };

    // Browsers cannot be asked for a PDF without going through the print dialog, so we
    // rasterise the factsheet ourselves and slice the resulting image across A4 pages.
    const downloadPdf = async () => {
        const body = iframeRef.current?.contentDocument?.body;
        if (!body || generatingPdf) return;

        setGeneratingPdf(true);
        try {
            // ~140kB gzipped, and only a few countries have a factsheet: keep it out of the
            // main bundle and pay for it only when someone actually asks for a PDF.
            const { jsPDF } = await import("jspdf");

            const width = body.scrollWidth;
            const height_ = body.scrollHeight;
            // Canvases are capped at roughly 16M pixels on Safari, and the factsheet is a
            // tall single-column document, so trade sharpness for surviving the capture.
            const pixelRatio = Math.min(2, Math.sqrt(MAX_CANVAS_PIXELS / (width * height_)));

            const canvas = await toCanvas(body, {
                backgroundColor: "#ffffff",
                width,
                height: height_,
                pixelRatio,
            });
            // JPEG rather than PNG: jsPDF embeds it as-is, while a PNG gets decoded and
            // stored uncompressed, which turned a five-page factsheet into a 48MB file.
            const dataUrl = canvas.toDataURL("image/jpeg", 0.95);

            const pdf = new jsPDF({ unit: "pt", format: "a4", compress: true });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const contentWidth = pageWidth - PAGE_MARGIN * 2;
            const contentHeight = pageHeight - PAGE_MARGIN * 2;
            const scale = contentWidth / canvas.width;
            const imageHeight = canvas.height * scale;
            const breaks = findPageBreaks(canvas, Math.floor(contentHeight / scale));

            let start = 0;
            breaks.forEach((end, page) => {
                if (page > 0) pdf.addPage();
                const sliceHeight = (end - start) * scale;
                // The shared alias keeps the bitmap as a single embedded object: every page
                // draws the same image, shifted up so its own slice lands inside the margins.
                pdf.addImage(
                    dataUrl,
                    "JPEG",
                    PAGE_MARGIN,
                    PAGE_MARGIN - start * scale,
                    contentWidth,
                    imageHeight,
                    "factsheet"
                );
                // addImage only clips at the page edge, so the rest of the document bleeds
                // past this page's slice: paint the margins and the tail back over it.
                pdf.setFillColor(255, 255, 255);
                pdf.rect(0, 0, pageWidth, PAGE_MARGIN, "F");
                pdf.rect(0, PAGE_MARGIN + sliceHeight, pageWidth, pageHeight - PAGE_MARGIN - sliceHeight, "F");
                start = end;
            });

            pdf.save(`${iso3}_factsheet.pdf`);
        } catch (error) {
            console.error("Factsheet PDF generation failed", error);
        } finally {
            setGeneratingPdf(false);
        }
    };

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                    px: 2,
                    py: 1,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Tooltip title="Print factsheet">
                        <IconButton aria-label="Print factsheet" onClick={printFactsheet}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Download as PDF">
                        <span>
                            <IconButton
                                aria-label="Download as PDF"
                                onClick={downloadPdf}
                                disabled={generatingPdf}
                            >
                                {generatingPdf ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    <PictureAsPdfOutlinedIcon />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Copy link to this factsheet">
                        <IconButton aria-label="Copy link to this factsheet" onClick={copyLink}>
                            <LinkOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                    <Box component="span" sx={{ fontSize: "1.1rem", fontWeight: 500 }}>
                        {title}
                    </Box>
                </Box>
                {endActions}
            </Box>

            <Box
                component="iframe"
                ref={iframeRef}
                src={url}
                title={title}
                sx={{ display: "block", width: "100%", height, border: 0 }}
            />

            <Snackbar
                open={linkCopied}
                autoHideDuration={3000}
                onClose={() => setLinkCopied(false)}
                message="Link copied to clipboard"
            />
        </>
    );
}
